import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Composer } from '../../components/Composer';
import { api, isAbort, session } from '../../shell/bridge';
import { markHintSeen } from '../../lib/hints';
/* Card psHEfEkK9cT2 — the Settings › You confetti switch governs these
   particles too: one preference, every celebration. */
import { useConfettiEnabled } from '../../lib/celebrate';
import { Pressable } from '../../ui/Pressable';
import { SkeletonBlock } from '../../ui/Skeleton';
import { Tooltip } from '../../ui/Tooltip';
import './requests.css';

interface RecurrenceRule {
  freq: 'daily' | 'weekly' | 'monthly';
  nextDueAt: number;
}

/** Which view a request lives in (design v1.1 §6). Rows written before the
 *  column existed read as 'life' — they already appear in Life, so that is
 *  where they stay (card DlK33bskwmPe). */
type RequestLane = 'life' | 'us';

interface RequestItem {
  requestId: string;
  fromMemberId: string;
  toMemberId: string | null;
  text: string;
  state: 'posted' | 'countered' | 'accepted' | 'declined' | 'snoozed' | 'done';
  /** life = the Life view, us = the Us view. */
  lane: RequestLane;
  /** Set only while state === 'snoozed': when the ask comes back. */
  snoozedUntil: number | null;
  counterText: string | null;
  recurrence: RecurrenceRule | null;
  createdAt: number;
  updatedAt: number;
  spawnedFromId: string | null;
  /** Members who gave kudos on this completed row — at most one entry each,
   *  never the member who did the work (card HBYCnnD9q5FS). */
  kudosBy: string[];
  kudosAt: number | null;
  /** Who completed the row; that member gets no kudos button for it. */
  completedBy: string | null;
}

const STATE_TONE: Record<RequestItem['state'], string> = {
  posted: 'is-pending',
  countered: 'is-countered',
  accepted: 'is-accepted',
  declined: 'is-declined',
  snoozed: 'is-snoozed',
  done: 'is-done',
};
const STATE_LABEL: Record<RequestItem['state'], string> = {
  posted: 'Pending',
  countered: 'Countered',
  accepted: 'Accepted',
  declined: 'Declined',
  snoozed: 'Snoozed',
  done: 'Done',
};

const REPEAT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'No repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

/** One-tap asks (card RLS5ba6EPD6l): a template fills the composer, Post
 *  sends it — a complete request in two taps and no typing. The label is what
 *  the couple reads on the chip; `text` is the ask itself, so "Lie-in ×2/week"
 *  keeps its promise in words (recurrence only knows daily/weekly/monthly). */
const TEMPLATES: { id: string; label: string; text: string }[] = [
  { id: 'liein', label: 'Lie-in ×2/week', text: 'A lie-in ×2/week' },
  { id: 'dinner', label: 'Handle dinner tonight', text: 'Handle dinner tonight' },
  { id: 'dog', label: 'Take the dog out', text: 'Take the dog out' },
  { id: 'date', label: 'Plan a date night', text: 'Plan a date night' },
];

/** "Yes, but…" amends (card RLS5ba6EPD6l). Every route posts a counter to the
 *  asker with no free text: a day chip or "Part of it" sends it in one tap,
 *  and only "Something else" ever asks for words. */
const AMEND_DAYS = ['Today', 'Tomorrow', 'This weekend', 'Next week'];

/** How long the first `/api/requests` read may hold the skeleton before it is
 *  treated as a failure (card 5f1XfZmgqtdr). 20s, not 10: a cold or loaded
 *  local worker routinely takes 7-15s (measured 2026-10-28), and failing a read
 *  that would have landed paints a false "Could not load requests" in the
 *  user's face. One retry follows, so a genuinely hung read still ends in the
 *  error state — bounded at ~40s instead of never. */
const REQUESTS_READ_TIMEOUT_MS = 20_000;

/** Open panel of the yes-but menu for one row: nothing open, the three
 *  amends, the day picker, or the free-text escape hatch. */
type YesButMode = '' | 'menu' | 'day' | 'text';

/**
 * Requests panel — composer is a single card (full-width input, repeat as
 * chip options, Post right-aligned), posted asks carry a status chip, and the
 * empty state invites a real ask instead of apologising.
 */
export function RequestsPanel({
  intent,
  lane = 'life',
  hideDone = false,
}: {
  intent?: { kind: 'ask' } | null;
  /** Which lane this panel files new asks into and shows rows for
   *  (design v1.1 §6): 'life' for Life, 'us' for the Us view. */
  lane?: RequestLane;
  /** "Needs you" hides closed rows — done work is not something you still
   *  need to do (card DlK33bskwmPe). */
  hideDone?: boolean;
}) {
  const me = session();
  const [items, setItems] = useState<RequestItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [freq, setFreq] = useState('');
  const [posting, setPosting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [counterDrafts, setCounterDrafts] = useState<Record<string, string>>({});
  /** Which row has its "Yes, but…" menu open, and which panel is showing. */
  const [yesBut, setYesBut] = useState<Record<string, YesButMode>>({});
  /** Row that just received kudos — drives the one-shot celebration toast. */
  const [celebrated, setCelebrated] = useState<string | null>(null);

  // Compose menu deep-link (design v1.1 §5.2): Ask routes here AND focuses the
  // new-request input so the user lands directly on the action. Intent is set
  // before the tab switch, so the panel mounts with it present — [] is right;
  // a dep'd effect would re-fire on every render and fight the caret.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const intentRef = useRef(intent);
  intentRef.current = intent;
  useEffect(() => {
    if (intentRef.current?.kind === 'ask') composerRef.current?.focus();
  }, []);

  const reload = useCallback(async () => {
    /* A read that never settles used to leave this panel on its skeleton for
       ever — no error, no retry, and the posted row only appeared after a
       reload (card 5f1XfZmgqtdr). Bound it: 10s, then one retry, then the
       error state. The first read on a cold worker genuinely takes seconds, so
       the retry is not a retry of a real failure. */
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const data = await api<{ requests: RequestItem[] }>('/api/requests', {
          timeoutMs: REQUESTS_READ_TIMEOUT_MS,
        });
        setItems(data.requests ?? []);
        setFailed(false);
        return;
      } catch (e) {
        // A page teardown cancels this request; that is not a load failure and
        // must not paint Requests as broken, nor log an outage the moment the
        // user leaves the screen (card TInC1EKQSF9t, same guard as
        // Settings/Interests/Digest — card d0Md65M0shSI).
        if (isAbort(e)) return;
        if (attempt === 0) continue;
        console.error('[Pear] requests load failed', e);
        setItems([]);
        setFailed(true);
      }
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* Retry from the error state: go back to the skeleton so a slow second try
     does not look like nothing happened. */
  const retry = useCallback(() => {
    setFailed(false);
    setItems(null);
    void reload();
  }, [reload]);

  // First-move hint (Rl0vzkZ2hdAe): "ask for one small thing" stops rendering
  // once THIS lane holds one — the hint names the lane it sits above, so a
  // request filed in the other view does not retire it.
  useEffect(() => {
    if (items && items.some(r => r.lane === lane)) markHintSeen('life');
  }, [items, lane]);

  // Live from the room socket: when the partner gives kudos, this list
  // refreshes without waiting for the next poll or click (the DO broadcasts
  // t:'requests'; useThread turns that into this event).
  useEffect(() => {
    const onRequests = () => void reload();
    /* A reconnect re-settles a list that was waiting on the room socket: the
       read can be issued around presence hydration, and an aborted first read
       never reaches the requests event (card 5f1XfZmgqtdr). */
    const onReconnected = () => void reload();
    window.addEventListener('pear:requests', onRequests);
    window.addEventListener('pear:reconnected', onReconnected);
    return () => {
      window.removeEventListener('pear:requests', onRequests);
      window.removeEventListener('pear:reconnected', onReconnected);
    };
  }, [reload]);

  const post = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const body: Record<string, unknown> = { text, lane };
      if (freq) body.recurrence = { freq, nextDueAt: Date.now() + 60_000 };
      await api('/api/requests', { body });
      setDraft('');
      setFreq('');
      await reload();
    } catch (err) {
      console.error('[Pear] post failed', err);
    } finally {
      setPosting(false);
    }
  };

  const transition = async (item: RequestItem, action: 'accept' | 'counter' | 'decline') => {
    if (busyId) return;
    const body: Record<string, unknown> = { action };
    if (action === 'counter') {
      const text = (counterDrafts[item.requestId] ?? '').trim();
      if (!text) return;
      body.counterText = text;
    }
    setBusyId(item.requestId);
    try {
      await api(`/api/requests/${item.requestId}/transition`, { body });
      setCounterDrafts(prev => {
        const next = { ...prev };
        delete next[item.requestId];
        return next;
      });
      setYesBut(prev => {
        const next = { ...prev };
        delete next[item.requestId];
        return next;
      });
      await reload();
    } catch (e) {
      console.error('[Pear] transition failed', e);
    } finally {
      setBusyId(null);
    }
  };

  /** "Yes, but…" (card RLS5ba6EPD6l): amend WITHOUT typing. The server only
   *  knows one amendment — a counter — so the three amends are three ways of
   *  writing it, and the asker always receives it as a counter-request on the
   *  row they filed. On a failure the row reloads instead of pretending. */
  const amend = async (item: RequestItem, counterText: string) => {
    if (busyId) return;
    setBusyId(item.requestId);
    try {
      await api(`/api/requests/${item.requestId}/transition`, {
        body: { action: 'counter', counterText },
      });
      setYesBut(prev => {
        const next = { ...prev };
        delete next[item.requestId];
        return next;
      });
      await reload();
    } catch (e) {
      console.error('[Pear] amend failed', e);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** One button per row: open the menu, open a panel inside it, close it. */
  const setYesButMode = (requestId: string, mode: YesButMode) =>
    setYesBut(prev => ({ ...prev, [requestId]: mode }));

  /** Praise the finished work. The server enforces the rules, so a refusal is
   *  information, not a bug: reload and show the row's true state. */
  const giveKudos = async (item: RequestItem) => {
    if (busyId) return;
    setBusyId(item.requestId);
    try {
      await api(`/api/requests/${item.requestId}/kudos`, { body: {} });
      setCelebrated(item.requestId);
      window.setTimeout(
        () => setCelebrated(c => (c === item.requestId ? null : c)),
        4000,
      );
      await reload();
    } catch (e) {
      console.error('[Pear] kudos failed', e);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Lane view: only this panel's lane, and (for "Needs you") no closed rows.
   *  Rows written before lane existed read as 'life', so they stay put. */
  const visible = (items ?? []).filter(item => {
    if ((item.lane ?? 'life') !== lane) return false;
    if (hideDone && item.state === 'done') return false;
    return true;
  });

  /** Asks waiting on THIS member's decision (card o4i7UUbHTn5M): an open ask
   *  from your partner, or a counter that came back to you. These used to sit
   *  mixed into the list under the screen's first heading — the founder read
   *  that as the wrong order, so they render as one named group BELOW it. */
  const needsYou = visible.filter(
    item =>
      !!me &&
      item.state !== 'done' &&
      item.state !== 'snoozed' &&
      ((item.state === 'posted' && item.fromMemberId !== me.memberId) ||
        (item.state === 'countered' && item.fromMemberId === me.memberId)),
  );
  const rest = visible.filter(item => !needsYou.includes(item));

  /* One renderer for both groups (card o4i7UUbHTn5M): the asks that are
     waiting on YOU draw below the list, in their own group, and they are
     the same rows with the same controls — one renderer stops the two
     groups drifting apart. */
  const renderRow = (item: RequestItem) => {
    const isAuthor = !!me && item.fromMemberId === me.memberId;
    const snoozed = item.state === 'snoozed';
    const closed = item.state === 'done';
    // Snoozed and done never offer the accept/counter controls: one is
    // waiting for its return date, the other is closed (DlK33bskwmPe).
    const actionable =
      !snoozed &&
      !closed &&
      ((item.state === 'posted' && !isAuthor) || (item.state === 'countered' && isAuthor));
    // Amending only exists on an OPEN ask from your partner: a counter
    // that came back can be accepted or declined, never countered again
    // (REQUEST_TRANSITIONS), so the counter controls stay off it — the
    // old always-rendered Counter button there failed server-side.
    const needsDecision = item.state === 'posted' && !isAuthor;
    const ybMode: YesButMode = yesBut[item.requestId] ?? '';
    const busy = busyId === item.requestId;
    // Kudos only on completed work, never for your own doing, never
    // twice (card HBYCnnD9q5FS). completedBy is null for rows accepted
    // before this shipped — those take kudos from either partner.
    const alreadyGiven = !!me && item.kudosBy.includes(me.memberId);
    const kudosable =
      item.state === 'accepted' && !!me && !alreadyGiven && item.completedBy !== me.memberId;
    return (
      <div
        className={`pear-request-row rq-row${closed ? ' is-done' : ''}`}
        key={item.requestId}
      >
        <div className="rq-head">
          <span className={`rq-state ${STATE_TONE[item.state]}`}>{STATE_LABEL[item.state]}</span>
          <span className="rq-when">{new Date(item.createdAt).toLocaleDateString()}</span>
        </div>

        <p className="pear-request-text rq-text">{item.text}</p>

        {item.counterText && <p className="pear-request-counter rq-counter">Counter: {item.counterText}</p>}
        {item.recurrence && <span className="rq-recurrence">↺ {item.recurrence.freq}</span>}

        {/* Snoozed: the accept/counter controls step aside for a quiet
            dated line — deferred, not lost (card DlK33bskwmPe). */}
        {snoozed && (
          <p className="rq-snoozed" data-testid="rq-snoozed">
            {item.snoozedUntil
              ? `Snoozed until ${new Date(item.snoozedUntil).toLocaleDateString()}`
              : 'Snoozed — waiting for its return date.'}
          </p>
        )}

        {/* The thanks still happened: a closed row keeps its kudos trail
            (mark only — the button is an action, and done has none). */}
        {(item.state === 'accepted' || (closed && item.kudosBy.length > 0)) && (
          <div className="rq-kudos-row">
            {item.kudosBy.length > 0 && (
              <span className="rq-kudos-mark" data-testid="kudos-mark">
                <span aria-hidden="true">🫂</span>
                {alreadyGiven ? ' You gave kudos' : ' Kudos'}
                {item.kudosAt ? ` · ${new Date(item.kudosAt).toLocaleDateString()}` : ''}
              </span>
            )}
            {kudosable && (
              <Pressable
                className="pear-btn rq-kudos"
                disabled={busy}
                aria-label="Give kudos"
                onClick={() => void giveKudos(item)}
              >
                <span aria-hidden="true">🫂</span> Kudos
              </Pressable>
            )}
          </div>
        )}

        {actionable && (
          <div className="rq-actions">
            <Pressable
              className="pear-btn pear-btn-mustard rq-action"
              disabled={busy}
              onClick={() => void transition(item, 'accept')}
            >
              Accept
            </Pressable>
            <Tooltip label="Say yes and it marks the work done, so the asker does not have to chase. For example: you accept the pharmacy run — it drops off both your lists once you have done it." tipFor="accept" />

            {/* Card RLS5ba6EPD6l — the amendment lives behind ONE button
                and every route through it sends a counter to the asker
                without a keystroke. It only appears on an open ask from
                your partner, so nothing here can hit an illegal
                transition. */}
            {needsDecision && (
              <Pressable
                className="pear-btn rq-action rq-action-plain rq-yesbut-toggle"
                disabled={busy}
                data-testid="rq-yesbut-toggle"
                aria-expanded={ybMode !== ''}
                aria-label="Yes, but — amend this ask"
                onClick={() => setYesButMode(item.requestId, ybMode === '' ? 'menu' : '')}
              >
                Yes, but…
              </Pressable>
            )}

            <Pressable
              className="pear-btn pear-btn-blush rq-action"
              disabled={busy}
              onClick={() => void transition(item, 'decline')}
            >
              Decline
            </Pressable>
            <Tooltip label="Say no and it closes with a reason if you send one — nothing hangs over you. For example: decline the Saturday shop with 'working, sorry' — it is off the list, not your conscience." tipFor="decline" />
          </div>
        )}

        {/* Three amends, zero typing (card RLS5ba6EPD6l). "Part of it"
            sends straight from the menu; the other two open one more
            pick and send there. */}
        {actionable && needsDecision && ybMode === 'menu' && (
          <div className="rq-yesbut-menu" data-testid="rq-yesbut-menu" role="group" aria-label="Amend the ask">
            <button
              type="button"
              className="rq-yesbut-opt"
              data-testid="rq-yb-day"
              onClick={() => setYesButMode(item.requestId, 'day')}
            >
              A different day
            </button>
            <button
              type="button"
              className="rq-yesbut-opt"
              data-testid="rq-yb-partial"
              disabled={busy}
              onClick={() => void amend(item, 'Yes, but only part of it')}
            >
              Part of it
            </button>
            <button
              type="button"
              className="rq-yesbut-opt"
              data-testid="rq-yb-text"
              onClick={() => setYesButMode(item.requestId, 'text')}
            >
              Something else
            </button>
          </div>
        )}

        {actionable && needsDecision && ybMode === 'day' && (
          <div className="rq-yesbut-menu" data-testid="rq-yesbut-days" role="group" aria-label="Amend the day">
            <span className="rq-yesbut-lead">Different day —</span>
            {AMEND_DAYS.map((day, i) => (
              <button
                key={day}
                type="button"
                className="rq-yesbut-opt"
                data-testid={`rq-yb-day-${i}`}
                disabled={busy}
                onClick={() => void amend(item, `Yes, but ${day.toLowerCase()} instead`)}
              >
                {day}
              </button>
            ))}
          </div>
        )}

        {/* The only amend that asks for words — and even here the send
            is a button, not Enter-key lore. */}
        {actionable && needsDecision && ybMode === 'text' && (
          <div className="rq-yesbut-menu rq-yesbut-text" data-testid="rq-yesbut-text">
            <input
              type="text"
              className="pear-chat-input rq-counter-input"
              placeholder="Say it your way…"
              aria-label="Counter suggestion"
              autoFocus
              value={counterDrafts[item.requestId] ?? ''}
              onChange={e => setCounterDrafts(prev => ({ ...prev, [item.requestId]: e.target.value }))}
            />
            <Pressable
              className="pear-btn rq-action rq-action-plain"
              disabled={busy || !(counterDrafts[item.requestId] ?? '').trim()}
              onClick={() => void transition(item, 'counter')}
            >
              Counter
            </Pressable>
          </div>
        )}
      </div>
    );
  };

  return (
    /* The heading belongs to the screen, not the panel (card o4i7UUbHTn5M):
       Life draws "Requests" above this panel already, and a second title two
       lines down read as a duplicate rather than as a section. */
    <div className="rq-panel">

      <form className="rq-card rq-composer" onSubmit={post}>
        <label className="rq-composer-label" htmlFor="rq-input">
          Ask for something
        </label>

        {/* Card RLS5ba6EPD6l — templates instead of a blank field. Tapping a
            chip writes the ask for you; Post sends it, so a full request is
            two taps and no keystrokes. Underline selectors, like every other
            pick on this screen (founder 2026-10-08), never pills. */}
        <div className="rq-templates" role="group" aria-label="Ask from a template">
          {TEMPLATES.map(t => (
            <button
              key={t.id}
              type="button"
              data-testid={`rq-template-${t.id}`}
              aria-pressed={draft.trim() === t.text}
              className={`rq-template${draft.trim() === t.text ? ' is-on' : ''}`}
              onClick={() => {
                setDraft(t.text);
                composerRef.current?.focus();
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Bare composer: the rq-card form already owns templates + repeat
            chips, so the shared primitive renders only the field. Enter is a
            LINE BREAK now (founder email 2026-10-21) — Post submits, and
            Shift+Enter still breaks a line (card vUak8P9oPODW). */}
        <Composer
          id="rq-input"
          bare
          textAreaRef={composerRef}
          inputClassName="pear-chat-input rq-input"
          placeholder="e.g. a lie-in on Saturday"
          ariaLabel="New request"
          value={draft}
          onChange={v => setDraft(v)}
        />
        <div className="rq-composer-foot">
          {/* One dropdown, not four chips (report 2026-11-03, card
              dCBxMeiivtuW): at 360px the chip row wrapped the monthly option
              onto a second line. A native select keeps the foot on one row and
              still stores the exact same value ('', daily, weekly, monthly). No
              visible "Repeat" caption: at 360px the caption plus the ⓘ hint was
              enough to push the hint onto a second row, and the select's own
              value already says what it does. */}
          <label className="rq-repeat-select">
            <select
              className="pear-chat-input rq-repeat"
              data-testid="rq-repeat"
              aria-label="Repeat"
              value={freq}
              onChange={e => setFreq(e.target.value)}
            >
              {REPEAT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <button type="submit" className="pear-btn pear-btn-mustard rq-post" disabled={posting || draft.trim().length === 0}>
            {posting ? 'Posting…' : 'Post'}
          </button>
          <Tooltip label="Puts it on their list instead of buried in chat — they see it straight away and it stays visible until it is done. For example: 'Run together this week?' — they tick it off once you have." tipFor="post" />
        </div>
      </form>

      <div className="rq-list">
        {items === null && !failed && <SkeletonBlock lines={3} />}

        {failed && (
          <div className="rq-empty-state rq-failed" data-testid="rq-failed">
            <p className="rq-empty-title">Could not load requests.</p>
            <p className="rq-empty-hint">Your asks are safe — they just did not arrive.</p>
            <button type="button" className="pear-btn pear-btn-mustard rq-retry" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        {items !== null && visible.length === 0 && !failed && (
          <div className="rq-empty-state">
            <p className="rq-empty-title">No requests yet.</p>
            <p className="rq-empty-hint">Ask for something small — a lie-in, a shared dinner, an evening walk.</p>
          </div>
        )}

        {rest.map(renderRow)}
      </div>

      {/* Asks waiting on YOUR decision sit BELOW the list under their own
          heading (founder email 2026-10-25, card o4i7UUbHTn5M): the screen is
          "Requests"; what needs you is the last thing on it, not the first
          block you scroll past. Outside .rq-list on purpose — a row inside
          that list is "in the list", which is the order the founder rejected. */}
      {needsYou.length > 0 && (
        <>
          <h3 className="rq-needs-title">Needs you</h3>
          <div className="rq-needs" data-testid="rq-needs-you">
            {needsYou.map(renderRow)}
          </div>
        </>
      )}

      {/* One-shot celebration (card HBYCnnD9q5FS): the same praise the thread
          gives, here where the work happened. Particles are decoration — the
          toast itself carries the message, and reduced motion keeps it. */}
      {celebrated && <KudosToast />}
    </div>
  );
}

/** The celebration primitive (card HBYCnnD9q5FS) — one fixed toast plus ten
 *  particles. Exported rather than copied so every surface that celebrates
 *  does it with the SAME element: the Chores tick-off reuses it verbatim.
 *
 *  Two jobs make the reuse honest (card FktjQ7CT4keo):
 *   - PORTAL: the toast mounts on `document.body`, never inside the panel.
 *     `.ch-panel` / `.pear-chat` carry a transform (the fade-in), and a
 *     transformed ancestor is the containing block for `position: fixed` —
 *     anchored in there, `bottom: 96px` resolved against a 1075px panel and
 *     the toast landed at y=1022 on an 844px screen, under the card it was
 *     celebrating and below the fixed nav (z 150 vs the toast's 60).
 *   - `variant`: 'confetti' (kudos — glyph + falling particles) or
 *     'shimmer' (chore tick-off — text only, one light sweep, no sparkle). */
export function KudosToast({
  glyph = '🫂',
  label = 'Kudos sent',
  variant = 'confetti',
}: { glyph?: string; label?: string; variant?: 'confetti' | 'shimmer' }) {
  /* Card psHEfEkK9cT2 — the same preference the app-wide layer reads. Off
     keeps the toast (the message must still land) and drops the particles. */
  const confettiOn = useConfettiEnabled();
  const body = (
    <div
      className={`rq-kudos-toast${variant === 'shimmer' ? ' rq-kudos-toast--shimmer' : ''}`}
      role="status"
      data-testid="kudos-toast"
    >
      {/* An empty glyph renders nothing at all — the celebration primitive
          takes its symbol per surface (kudos keeps 🫂, the chore tick-off
          passes none), and a hollow gap where the emoji was reads worse than
          no slot. */}
      {glyph && (
        <span className="rq-kudos-toast-glyph" aria-hidden="true">
          {glyph}
        </span>
      )}
      <span className="rq-kudos-toast-text">{label}</span>
      {variant === 'confetti' && confettiOn &&
        Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className="rq-kudos-particle" aria-hidden="true" />
        ))}
    </div>
  );
  return createPortal(body, document.body);
}